import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Milk,
  Droplets,
  ClipboardList,
  Upload,
  TestTube,
  Activity,
  AlertCircle,
  RefreshCw,
} from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import api from '../../services/api'
import StatCard from '../../components/StatCard'
import PageHeader from '../../components/PageHeader'

// ---------------------------------------------------------------------------
// API helpers (to be moved to api.js later)
// ---------------------------------------------------------------------------

const getMilkRecordingSummary = (ranchId, dateFrom, dateTo) =>
  api.get('/dairy/milk-recordings/summary', {
    params: { ranch_id: ranchId, date_from: dateFrom, date_to: dateTo },
  }).then(res => res.data)

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function last14DaysRange() {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - 13)
  const fmt = (d) => d.toISOString().split('T')[0]
  return { dateFrom: fmt(from), dateTo: fmt(to) }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function DairyDashboard({ ranchId }) {
  const { dateFrom, dateTo } = last14DaysRange()

  const { data: summary, isLoading, error, refetch } = useQuery({
    queryKey: ['dairySummary', ranchId, dateFrom, dateTo],
    queryFn: () => getMilkRecordingSummary(ranchId, dateFrom, dateTo),
  })

  // ---- Quick Links ----
  const quickLinks = [
    { label: 'Record Session', icon: ClipboardList, path: '/dairy/record-session', bg: 'bg-emerald-50 hover:bg-emerald-100', text: 'text-emerald-700' },
    { label: 'Import CSV', icon: Upload, path: '/dairy/import-csv', bg: 'bg-blue-50 hover:bg-blue-100', text: 'text-blue-700' },
    { label: 'Quality Tests', icon: TestTube, path: '/dairy/quality', bg: 'bg-purple-50 hover:bg-purple-100', text: 'text-purple-700' },
    { label: 'Lactation Overview', icon: Activity, path: '/dairy/lactation', bg: 'bg-amber-50 hover:bg-amber-100', text: 'text-amber-700' },
  ]

  // ---- Loading / Error ----
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card p-6 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900">Failed to load dairy dashboard</h3>
        <p className="text-gray-500 mt-1">{error.message}</p>
        <button onClick={() => refetch()} className="btn-primary mt-4">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </button>
      </div>
    )
  }

  const dailyProduction = summary?.daily_production || []
  const todayMilk = summary?.today_total_liters ?? 0
  const yieldPerCow = summary?.yield_per_cow ?? 0
  const milkingCows = summary?.milking_cows ?? 0
  const bulkTankScc = summary?.bulk_tank_scc ?? '--'

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dairy Dashboard"
        subtitle="Milk production overview for the last 14 days"
        icon={Milk}
      />

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Daily Milk"
          value={`${Number(todayMilk).toFixed(1)} L`}
          icon={Droplets}
          color="blue"
        />
        <StatCard
          title="Yield / Cow"
          value={`${Number(yieldPerCow).toFixed(1)} L`}
          subtitle="per cow per day"
          icon={Activity}
          color="green"
        />
        <StatCard
          title="Milking Cows"
          value={milkingCows}
          icon={Milk}
          color="purple"
        />
        <StatCard
          title="Bulk Tank SCC"
          value={typeof bulkTankScc === 'number' ? bulkTankScc.toLocaleString() : bulkTankScc}
          subtitle="cells/mL"
          icon={TestTube}
          color={typeof bulkTankScc === 'number' && bulkTankScc > 400000 ? 'red' : 'primary'}
        />
      </div>

      {/* Quick Links */}
      <div className="card p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {quickLinks.map((action) => (
            <Link
              key={action.path}
              to={action.path}
              className={`flex items-center gap-2 p-3 rounded-lg text-sm font-medium transition-colors ${action.bg} ${action.text}`}
            >
              <action.icon className="h-4 w-4" />
              {action.label}
            </Link>
          ))}
        </div>
      </div>

      {/* Daily Production Chart */}
      <div className="card p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Daily Production (Last 14 Days)</h3>
        {dailyProduction.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Droplets className="h-10 w-10 mx-auto mb-3 text-gray-300" />
            <p>No milk recordings found for this period</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dailyProduction} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                tickFormatter={(val) => {
                  const d = new Date(val)
                  return `${d.getDate()}/${d.getMonth() + 1}`
                }}
              />
              <YAxis tick={{ fontSize: 12 }} unit=" L" />
              <Tooltip
                formatter={(value) => [`${Number(value).toFixed(1)} L`, 'Production']}
                labelFormatter={(label) => new Date(label).toLocaleDateString()}
              />
              <Bar dataKey="total_liters" fill="#10b981" radius={[4, 4, 0, 0]} name="Production" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}
